/* eslint-disable max-classes-per-file */
/* JavaScript for DonationForm */
import AdyenCheckout from "@adyen/adyen-web";
import Component from "../component";
import { Screen } from "./screen";

const getCsrfToken = () =>
  document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");

// Export the class itself
export class DonationForm extends Component {
  get currentScreen() {
    return this.screens[this.state.currentScreen];
  }

  get defaultState() {
    return {
      currentScreen: 0,
      result: "pending",
      adyenData: {},
    };
  }

  constructor(...props) {
    super(...props);

    this.screens = [...this.elements.screens].map(
      (element, index) => new Screen({ element, index, donationForm: this })
    );
    this.screens.forEach((screen) => {
      screen.on("edit", ({ index }) => {
        // If the request to edit is for the current screen, do nothing
        if (this.state.currentScreen === index) {
          return;
        }

        // If the request to edit is for the next screen, check if the current screen is valid
        if (index > this.state.currentScreen && !this.currentScreen.isValid()) {
          this.currentScreen.updateErrors();
          return;
        }

        this.goTo(index);
      });

      screen.on("next", this.next.bind(this));
    });
    this.inputs = this.screens.map((s) => s.inputs).flat();
    this.inputsByName = this.screens.reduce((acc, screen) => {
      return {
        ...acc,
        ...screen.inputsByName,
      };
    }, {});
    this.inputs.forEach((input) => {
      input.on("reveal", ({ revealedState }) => {
        if (revealedState) {
          this.revealScreens(revealedState);
        }
      });
      input.checkValidity();
    });

    this.applePayAvailable = false;
    this.initializePaymentMethodSelection();

    this.setUpAdyen();

    window.donationForm = this;
  }

  async setUpAdyen() {
    const { paymentMethodsEndpoint, clientKey, environment, applePaySessionEndpoint } =
      this.elements.form.dataset;
    // Get the available payment methods from the server
    const { paymentMethods } = await fetch(paymentMethodsEndpoint).then((res) =>
      res.json()
    );
    const brands =
      paymentMethods.find((method) => method.type === "scheme")?.brands ?? [];

    // Create the Adyen client
    this.checkout = await AdyenCheckout({
      locale: "en_US",
      environment,
      clientKey,
    });

    // Instantiate the Adyen Secured Fields card component
    const { fontSize, fontFamily } = window.getComputedStyle(
      this.elements.typeSample
    );
    this.customCard = this.checkout
      .create("securedfields", {
        // Optional configuration
        type: "card",
        brands,
        styles: {
          base: {
            color: "#315a3a",
            fontSize: fontSize,
            fontSmoothing: "antialiased",
            fontFamily: fontFamily,
          },
          error: {
            color: "#315a3a",
          },
          placeholder: {
            color: "transparent",
          },
          validated: {
            color: "#315a3a",
          },
        },
        // Events
        onChange: ({ data, errors, valid }) => {
          this.update({
            adyenData: data?.paymentMethod || {},
          });

          Object.entries(valid).forEach(([fieldType, fieldIsValid]) => {
            const input = this.findInputByName(fieldType);
            if (!input) {
              return;
            }

            input.setAdyenValidity(fieldIsValid);
          });
        },
        onFieldValid: ({ fieldType, valid }) => {
          const input = this.findInputByName(fieldType);
          if (!input) {
            return;
          }

          input.setAdyenValidity(valid);
        },
        onError: () => {
          this.update({ result: "failure" });
        },
        onFocus: (event) => {
          const { fieldType, focus: isFocused } = event;
          const input = this.findInputByName(fieldType);
          if (!input) {
            return;
          }

          input.markAsTouched();
          if (isFocused) {
            input.adyenFocus();
          } else {
            input.adyenBlur();
          }
        },
        onBinValue: function (bin) {},
        onBinLookup: function (callbackObj) {},
      })
      .mount("#customCard-container");

    let applePayAvailable = false;
    try {
      applePayAvailable = this.setupApplePay({
        paymentMethods,
        applePaySessionEndpoint,
      });
    } catch (error) {
      console.error("Error setting up Apple Pay", error);
      applePayAvailable = false;
    }
    this.updatePaymentMethodAvailability({
      applePayAvailable,
    });
  }

  revealScreens(revealedState) {
    this.screens.forEach((screen) => {
      screen.update({ revealed: revealedState });
    });
  }

  setUpElements() {
    super.setUpElements();

    this.elements.root = this.props.element;
    this.elements.form = this.props.element.querySelector(
      ".js-donation-form__form"
    );
    this.elements.screens = this.props.element.querySelectorAll(
      ".js-donation-form__screen"
    );
    this.elements.confirmation = this.props.element.querySelector(
      ".js-donation-form__confirmation"
    );
    this.elements.typeSample = this.props.element.querySelector(
      ".js-donation-form__type-sample"
    );
    this.elements.donationAmountNumber = this.props.element.querySelector(
      ".js-donation-form__donation-amount-number"
    );
    this.elements.donationBox = this.props.element.querySelector(
      ".js-donation-form__donation"
    );
    this.elements.donationAmountDisplay = this.props.element.querySelector(
      ".js-donation-form__donation-amount-display"
    );
    this.elements.adyenInputs = this.props.element.querySelector(
      ".js-donation-form__adyen-inputs"
    );
    this.elements.applePayButton = this.props.element.querySelector(
      ".js-donation-form__apple-pay-button"
    );
    this.elements.paymentMethodRadios = [
      ...this.props.element.querySelectorAll(
        ".js-donation-form__payment-method-radio"
      ),
    ];
    this.elements.applePayOption = this.props.element.querySelector(
      ".js-donation-form__payment-method-option--apple-pay"
    );
    this.elements.creditCardOption = this.props.element.querySelector(
      ".js-donation-form__payment-method-option--credit-card"
    );
    this.elements.creditCardIconBadge =
      this.elements.creditCardOption?.querySelector(".icon-badge");
    this.elements.paymentMethodGroup = this.props.element.querySelector(
      ".js-donation-form__payment .js-donation-form__payment-method"
    );
    this.elements.paymentFooter = this.props.element.querySelector(
      ".js-donation-form__payment .donate-module-screen__footer"
    );
    this.elements.applePayRadio =
      this.elements.applePayOption?.querySelector("input[type='radio']");
    this.elements.creditCardRadio =
      this.elements.creditCardOption?.querySelector("input[type='radio']");
    this.elements.creditCardFields = this.props.element.querySelector(
      ".js-donation-form__credit-card-fields"
    );
  }

  next() {
    if (this.state.currentScreen === this.screens.length - 1) {
      return;
    }

    if (!this.currentScreen.isValid()) {
      this.currentScreen.updateErrors();
      return;
    }

    this.update({ currentScreen: this.state.currentScreen + 1 });
  }

  previous() {
    if (this.state.currentScreen === 0) {
      return;
    }

    this.update({ currentScreen: this.state.currentScreen - 1 });
  }

  goTo(screen) {
    if (screen < 0 || screen >= this.screens.length) {
      return;
    }

    this.update({ currentScreen: screen });
  }

  formIsValid() {
    const invalidScreen = this.screens.find((screen) => !screen.isValid());
    if (invalidScreen) {
      this.goTo(invalidScreen.props.index);
      return false;
    }

    return true;
  }

  findScreenByInputName(name) {
    return this.screens.find((screen) => screen.inputs[name] !== undefined);
  }

  findInputByName(name) {
    return this.inputsByName[name];
  }

  // extract_amount("$1,250.45") # => "1250.45"
  extractValueFromDollar(str) {
    const match = str.match(/^\$?([\d.,]+)$/);
    if (!match) return null;

    const num = match[1].replace(/,/g, "");
    const floatVal = parseFloat(num);

    if (isNaN(floatVal)) return null;

    // Return as a "0.00" formatted string
    return floatVal.toFixed(2);
  }

  getDonationAmountValue() {
    const currentValue = this.inputsByName['donation[donation_amount]'].getValue()
    if (!currentValue) {
      return "0.00"
    }

    return this.extractValueFromDollar(currentValue)
  }

  initializePaymentMethodSelection() {
    this.creditCardInputs = [
      "encryptedCardNumber",
      "encryptedExpiryDate",
      "encryptedSecurityCode",
    ]
      .map((name) => this.findInputByName(name))
      .filter(Boolean);

    this.elements.paymentMethodRadios?.forEach((radio) => {
      radio.addEventListener("change", (event) => {
        this.handlePaymentMethodChange(event.target.value);
      });
    });

    this.hideCreditCardFields();
  }

  updatePaymentMethodAvailability({ applePayAvailable }) {
    this.applePayAvailable = applePayAvailable;

    if (applePayAvailable) {
      this.elements.applePayOption?.classList.remove("is-hidden");
      this.elements.applePayButton?.classList.remove("is-hidden");
      this.elements.paymentMethodGroup?.classList.remove("is-hidden");
      this.elements.paymentFooter?.classList.remove("is-visible");
    } else {
      this.elements.applePayOption?.classList.add("is-hidden");
      this.elements.applePayButton?.classList.add("is-hidden");
      this.elements.paymentMethodGroup?.classList.add("is-hidden");
      this.selectPaymentMethod("credit_card", { startApplePay: false });
      this.showCreditCardFields();
      this.elements.paymentFooter?.classList.add("is-visible");
    }
  }

  selectPaymentMethod(value, { startApplePay = true } = {}) {
    this.elements.paymentMethodRadios?.forEach((radio) => {
      const shouldCheck = radio.value === value;
      const wasChecked = radio.checked;
      radio.checked = shouldCheck;

      if (shouldCheck !== wasChecked) {
        radio.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    this.handlePaymentMethodChange(value, { startApplePay });
  }

  handlePaymentMethodChange(value, { startApplePay = true } = {}) {
    if (value === "applepay") {
      this.hideCreditCardFields();
      if (startApplePay && this.applePayAvailable) {
        this.startApplePayFlow();
      } else if (!this.applePayAvailable) {
        this.selectPaymentMethod("credit_card", { startApplePay: false });
      }
      return;
    }

    if (value === "credit_card") {
      this.showCreditCardFields();
    }
  }

  hideCreditCardFields() {
    this.elements.creditCardFields?.classList.add("is-hidden");
    this.creditCardInputs?.forEach((input) => {
      input.hide();
    });
    this.elements.creditCardIconBadge.classList.remove("color-default");
  }

  showCreditCardFields() {
    this.elements.creditCardFields?.classList.remove("is-hidden");
    this.creditCardInputs?.forEach((input) => {
      input.show();
      if (input.type === "adyen") {
        input.setAdyenValidity(false);
      }
    });
    this.elements.creditCardIconBadge.classList.add("color-default");
  }

  startApplePayFlow() {
    if (
      !this.applePayAvailable ||
      !this.applePayMethod ||
      !this.applePaySessionEndpoint ||
      !window.ApplePaySession
    ) {
      return;
    }

    const paymentRequest = {
      countryCode: "US",
      currencyCode: "USD",
      total: {
        label:
          this.applePayMethod.configuration?.merchantName || "Donation",
        amount: this.getDonationAmountValue(),
      },
      supportedNetworks: this.applePaySupportedNetworks || [],
      merchantCapabilities: ["supports3DS"],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = (event) => {
      this.validateApplePayMerchant({
        session,
        validationURL: event.validationURL,
        applePaySessionEndpoint: this.applePaySessionEndpoint,
      });
    };

    session.onpaymentauthorized = (event) => {
      this.handleApplePayPayment({ session, payment: event.payment });
    };

    session.oncancel = () => {
      // Optional: handle user cancel
    };

    session.begin();
  }

  setupApplePay({ paymentMethods, applePaySessionEndpoint }) {
    // Guardrails: need Apple Pay support and a backend endpoint
    this.applePayMethod = null;
    this.applePaySupportedNetworks = [];
    this.applePaySessionEndpoint = applePaySessionEndpoint;

    if (
      !window.ApplePaySession ||
      typeof window.ApplePaySession !== "function" ||
      !applePaySessionEndpoint
    ) {
      return false;
    }

    const applePayMethod = paymentMethods.find(
      (method) => method.type === "applepay"
    );
    if (!applePayMethod) {
      return false;
    }

    // Optional: further device capability check
    if (!ApplePaySession.canMakePayments()) {
      return false;
    }

    this.applePayMethod = applePayMethod;
    this.applePaySessionEndpoint = applePaySessionEndpoint;
    const adyenBrandNameToAppleNetworkName = {
      visa: "visa",
      mc: "masterCard",
      amex: "amex",
      discover: "discover",
    };

    this.applePaySupportedNetworks = (applePayMethod.brands || [])
      .map((brand) => adyenBrandNameToAppleNetworkName[brand])
      .filter(Boolean); // drop anything we can't map

    // Show the button if you were hiding it by default
    this.elements.applePayButton?.classList.remove("is-hidden");
    this.elements.applePayButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.startApplePayFlow();
    });

    return true;
  }

  validateApplePayMerchant({ session, validationURL, applePaySessionEndpoint }) {
    fetch(applePaySessionEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      credentials: "same-origin",
      body: JSON.stringify({
        validationURL,
        domainName: window.location.hostname,
      }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Apple Pay merchant validation failed");
        }
        return res.json();
      })
      .then((data) => {
        session.completeMerchantValidation(data.merchantSession);
      })
      .catch((error) => {
        console.error("Apple Pay merchant validation error", error);
        try {
          session.abort();
        } catch (e) {
          // ignore
        }
      });
  }

  collectFormData() {
    const form = this.elements.form;
    const formData = new FormData(form);
    const data = {};

    for (const [key, value] of formData.entries()) {
      // Handles Rails-style nested params like donation[name]
      if (key.includes("[")) {
        const match = key.match(/^([^\[]+)\[([^\]]+)\]$/);
        if (match) {
          const top = match[1];
          const child = match[2];
          data[top] = data[top] || {};
          data[top][child] = value;
          continue;
        }
      }

      data[key] = value;
    }

    return data;
  }

  handleApplePayPayment({ session, payment }) {
    const { applePayPaymentEndpoint } = this.elements.form.dataset;

    if (!applePayPaymentEndpoint) {
      console.error("Missing applePayPaymentEndpoint data attribute");
      session.completePayment(ApplePaySession.STATUS_FAILURE);
      return;
    }

    const token = payment?.token?.paymentData;

    // Collect all existing form fields
    const formDataObject = this.collectFormData();

    const payload = {
      ...formDataObject,
      applePayToken: token,
    };

    fetch(applePayPaymentEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
        "Accept": "text/vnd.turbo-stream.html"
      },
      credentials: "same-origin",
      body: JSON.stringify(payload),
    })
      .then((res) => res.text())
      .then((html) => {
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
        this.update({ result: "success" });
        Turbo.renderStreamMessage(html);

        // Wait one frame so Turbo can update the DOM
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      })
      .catch((error) => {
        console.error("Apple Pay payment error", error);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        this.update({ result: "failure" });
      });
  }

  render(update, previousState) {
    if (update.hasOwnProperty("currentScreen")) {
      if (typeof previousState.currentScreen === "number") {
        const previousScreen = this.screens[previousState.currentScreen];
        previousScreen.collapse();
        previousScreen.isValid();
        previousScreen.updateErrors();
      }
      this.screens[update.currentScreen].expand();
    }

    if (update.hasOwnProperty("result")) {
      this.elements.root.setAttribute("data-result", update.result);
    }

    if (update.hasOwnProperty("adyenData")) {
      this.elements.adyenInputs.innerHTML = "";
      Object.entries(this.state.adyenData).forEach(([key, value]) => {
        this.elements.adyenInputs.appendChild(
          Object.assign(document.createElement("input"), {
            type: "hidden",
            name: `adyen[${key}]`,
            value,
          })
        );
      });
    }
  }
}

// Exports an array of all the current instances
export const donationForms = {
  current: [],
};

// Export an init function that looks for and instantiates the module on pageload
export const init = () => {
  // Initialize any instances of the DonationForm on any given page
  document.addEventListener("turbo:frame-load", () => {
    donationForms.current = [
      ...document.querySelectorAll(".js-donation-form"),
    ].map((element) => new DonationForm({ element }));
  });
};
